import React from "react";
import styled, { css } from "styled-components";
import { microHeading, defaultFont } from "../theme/typography";
import { semiBold } from "../theme/typeface";
import { black300, black700, green300, white } from "../theme/colors";
import themeSizes from "../theme/themeSizes";

const LargeButton = styled.button`
  ${microHeading.bold}
  min-width: ${(props) => (props.boxWidth ? props.boxWidth : "125px")};
  cursor: ${(props) => (props.isDisabled ? "not-allowed" : "pointer")};
  height: ${(props) => (props.boxHeight ? props.boxHeight : "46px")};
  background: ${(props) => props.bgColor || "#2CAE66"};
  border-radius: ${(props) => (props.boxRadius ? props.boxRadius : "8px")};
  margin: ${(props) => props.margin || "0 12px 0 0"};
  font-weight: ${semiBold};
  color: ${(props) => props.color || "#FFFFFF"};
  outline: none;
  border: ${(props) => (props.border ? props.border : "none")};;
  cursor: pointer;
  padding: ${(props) => props.padding || "0 40px"};
  position: relative;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    &:not(:disabled) {
      filter: brightness(1.06);
      transform: translateY(-1px);
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
    }
  }

  &:active {
    &:not(:disabled) {
      transform: translateY(0);
      filter: brightness(0.96);
    }
  }

  ${(props) =>
    props.isExportCSV &&
    css`
      background: ${white} !important;
      border: 1px solid #2cae66 !important;
      color: #2cae66 !important;
      border-radius: 8px !important;
      font-weight: 600 !important;
      box-shadow: 0 2px 4px rgba(44, 174, 102, 0.05) !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 8px !important;

      span {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 8px !important;
        color: #2cae66 !important;
      }

      svg {
        margin-right: 0 !important;
        vertical-align: middle !important;
        width: 18px !important;
        height: 18px !important;
        color: #2cae66 !important;
        fill: #2cae66 !important;
      }

      &:hover {
        &:not(:disabled) {
          background: rgba(44, 174, 102, 0.04) !important;
          box-shadow: 0 4px 12px rgba(44, 174, 102, 0.1) !important;
          transform: translateY(-1px) !important;
          filter: none !important;
        }
      }

      &:active {
        &:not(:disabled) {
          transform: translateY(0) !important;
        }
      }
    `}

  ${(props) =>
    props.changeOnlyBgColor &&
    css`
      background: ${(props) => props.bgColor};
      border: 1px solid ${(props) => props.color || black700};
      color: ${(props) => props.color || black700};
    `}
  ${(props) =>
    props.isLoading &&
    css`
      padding: 0 35px 0 20px;
      cursor: not-allowed;
      transition: all 0.2s;
      &:after {
        content: " ";
        width: 16px;
        height: 16px;
        opacity: 0.6;
        right: 18px;
        top: 16px;
        border: 3px solid;
        border-radius: 100%;
        display: inline-block;
        position: absolute;
        background-clip: padding-box;
        border-left-color: transparent;
        animation: spin 1s linear infinite;
      }
    `};
  :disabled,
  &[disabled] {
    cursor: not-allowed;
    background: ${({ disabledBgColor }) => disabledBgColor || black300};
    box-shadow: none;
  }
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 995px) {
    min-height: 44px;
    &.cancel-button,
    &.save-button {
      width: 100% !important;
      min-width: 0 !important;
      margin: 0 !important;
      padding: 0 16px !important;
      display: inline-flex !important;
      justify-content: center !important;
      align-items: center !important;
      box-sizing: border-box !important;
    }
  }
`;

const MediumButton = styled.button`
  outline: none;
  position: relative;
  min-width: 92px;
  width: ${(props) => props.boxWidth};
  height: ${(props) => (props.boxHeight ? props.boxHeight : "39px")};
  padding: 0 16px;
  margin: ${(props) => props.margin || "0 12px 0 0"};
  ${defaultFont}
  font-weight : ${semiBold};
  color: ${(props) => props.color || black700};
  border-radius: ${(props) => (props.boxRadius ? props.boxRadius : "8px")};
  border: 1px solid ${(props) => props.color || black700};
  border: ${(props) => props.check === "ekyc" && props.border};
  cursor: ${(props) => (props.cursor ? props.cursor : "pointer")};
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);

  &:hover {
    &:not(:disabled) {
      filter: brightness(1.06);
      transform: translateY(-1px);
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
    }
  }

  &:active {
    &:not(:disabled) {
      transform: translateY(0);
      filter: brightness(0.96);
    }
  }

  ${(props) =>
    props.isExportCSV &&
    css`
      background: ${white} !important;
      border: 1px solid #2cae66 !important;
      color: #2cae66 !important;
      border-radius: 8px !important;
      font-weight: 600 !important;
      box-shadow: 0 2px 4px rgba(44, 174, 102, 0.05) !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;

      span {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 6px !important;
        color: #2cae66 !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
        max-width: 100% !important;
      }

      svg {
        margin-right: 0 !important;
        vertical-align: middle !important;
        width: 16px !important;
        height: 16px !important;
        flex-shrink: 0 !important;
        color: #2cae66 !important;
        fill: #2cae66 !important;
      }

      &:hover {
        &:not(:disabled) {
          background: rgba(44, 174, 102, 0.04) !important;
          box-shadow: 0 4px 12px rgba(44, 174, 102, 0.1) !important;
          transform: translateY(-1px) !important;
          filter: none !important;
        }
      }

      &:active {
        &:not(:disabled) {
          transform: translateY(0) !important;
        }
      }
    `}

  ${(props) =>
    props.isDisabled &&
    css`
      cursor: not-allowed;
    `};
  ${(props) =>
    props.noBorder &&
    css`
      border: none;
    `};
  ${(props) =>
    props.bgColor &&
    css`
      background: ${(props) => props.bgColor || green300};
      border: 1px solid ${(props) => props.bgColor || green300};
      border: ${(props) => props.check === "ekyc" && props.border};
      color: ${props.color || white};
    `};
  ${(props) =>
    props.changeOnlyBgColor &&
    css`
      background: ${(props) => props.bgColor};
      border: 1px solid ${(props) => props.color || black700};
      color: ${(props) => props.color || black700};
    `}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  span {
    position: relative;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    display: inline-block;
    vertical-align: middle;
  }
  svg {
    vertical-align: middle;
    position: relative;
    margin-right: 0;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
  :disabled,
  &[disabled] {
    cursor: not-allowed;
    opacity: 0.7;
    box-shadow: none;
  }
  ${(props) =>
    props.isLoading &&
    css`
      cursor: not-allowed;
      transition: all 0.2s;
      span {
        left: -9px;
      }
      &:after {
        content: " ";
        width: 16px;
        height: 16px;
        opacity: 0.6;
        right: 8px;
        top: 11px;
        border: 3px solid;
        border-radius: 100%;
        display: inline-block;
        position: absolute;
        background-clip: padding-box;
        border-left-color: transparent;
        animation: spin 1s linear infinite;
      }
    `};
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 768px) {
    min-height: 32px;
    height: ${(props) => (props.boxHeight ? props.boxHeight : "32px")};
    min-width: 0 !important;
    padding: 0 10px !important;
    font-size: 12px !important;
    letter-spacing: -0.25px !important;

    span {
      font-size: 12px !important;
      letter-spacing: -0.25px !important;
    }

    svg {
      width: 14px !important;
      height: 14px !important;
      margin-right: 2px !important;
    }
  }
`;

const SmallButton = styled(MediumButton)`
  width: ${({ boxWidth }) => boxWidth || "200px"};
  height: ${(props) => (props.boxHeight ? props.boxHeight : "39px")};

  @media (max-width: 768px) {
    min-height: 28px !important;
    height: ${(props) => (props.boxHeight ? props.boxHeight : "28px")} !important;
    padding: 0 8px !important;
    min-width: 0 !important;
    font-size: 11.5px !important;
    border-radius: 6px !important;

    span {
      font-size: 11.5px !important;
    }
  }
`;
export const Button = ({
  children,
  onClick = () => {},
  type = "button",
  isLoading = false,
  width = "max-content",
  borderRadius = "",
  fontStyle,
  isDisabled,
  size,
  bgColor,
  color,
  margin,
  noBorder,
  border = "",
  cursor,
  boxHeight,
  changeOnlyBgColor,
  disabledBgColor,
  padding = "",
  check,
  htmlFor,
  borderColor,
  title,
  className,
}) => {
  let ButtonWrapper = LargeButton;
  switch (size) {
    case "medium":
      ButtonWrapper = MediumButton;
      break;
    case "small":
      ButtonWrapper = SmallButton;
      break;
  }

  const checkHasExportText = (node) => {
    if (!node) return false;
    if (typeof node === "string" && node.toLowerCase().includes("export to csv")) {
      return true;
    }
    if (typeof node === "number") return false;
    if (Array.isArray(node)) {
      return node.some(child => checkHasExportText(child));
    }
    if (node.props && node.props.children) {
      return checkHasExportText(node.props.children);
    }
    return false;
  };
  const isExportCSV = checkHasExportText(children);


  return (
    <ButtonWrapper
      onClick={(e) => {
        e && e.preventDefault();
        if (!isLoading) {
          onClick && onClick(e);
        }
      }}
      isExportCSV={isExportCSV}
      type={type}
      check={check}
      isLoading={isLoading}
      boxWidth={width}
      boxRadius={borderRadius}
      style={fontStyle}
      disabled={isDisabled}
      bgColor={bgColor}
      color={color}
      margin={margin}
      title={title}  // <-- add this line

      noBorder={noBorder}
      border={border}
      cursor={cursor}
      boxHeight={boxHeight}
      changeOnlyBgColor={changeOnlyBgColor}
      disabledBgColor={disabledBgColor}
      padding={padding}
      htmlFor={htmlFor}
      borderColor={borderColor}
      className={className}
    >
      <span>{children}</span>
    </ButtonWrapper>
  );
};
